import {
  Component,
  computed,
  OnInit,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  IqPromptSelection,
  OutlookIqBoostStore,
  toCatalogPromptSelection,
} from '@maximizer/outlook/ai/data-access';
import { TrackerService } from '@maximizer/outlook/shared/data-access';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OutlookSuggestPromptComponent } from '../suggest-prompt/suggest-prompt.component';
import {
  OutlookNotificationComponent,
  OUTLOOK_SHARED_UI,
} from '@maximizer/outlook/shared/ui';
import {
  EventAction,
  EventJourney,
  EventModule,
} from '@maximizer/outlook/shared/domain';

interface IqPromptGroup {
  key: string;
  label: string;
  iconStyle: string;
  items: IqPromptSelection[];
}

@Component({
  selector: 'maximizer-outlook-iq-boost-question-library',
  templateUrl: './question-library.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    KENDO_DIALOG,
    KENDO_BUTTON,
    TranslatePipe,
    OutlookSuggestPromptComponent,
    OUTLOOK_SHARED_UI,
  ],
})
export class OutlookQuestionLibraryComponent implements OnInit {
  notification = viewChild<OutlookNotificationComponent>('notification');

  private readonly EMAIL_ASSIST_GROUP_KEY = 'email-assist';
  private readonly EMAIL_ASSIST_ICON_STYLE =
    'fa-icons icon-light icon-envelope';
  readonly groupCollapse = signal<Record<string, boolean>>({});
  readonly showSuggestPrompt = signal<boolean>(false);

  promptSelected = output<IqPromptSelection>();
  closeDialog = output<void>();

  // Categories arrive grouped, sorted, and localized from the server.
  groupedPrompts = computed<IqPromptGroup[]>(() => {
    const groups = this.iqBoostStore.promptCategories().map((category) => ({
      key: category.name,
      label: category.name,
      iconStyle: category.iconStyle,
      items: category.prompts.map(toCatalogPromptSelection),
    }));

    const emailAssistGroup = this.buildEmailAssistGroup();
    if (emailAssistGroup) groups.push(emailAssistGroup);

    return groups;
  });

  constructor(
    private readonly tracker: TrackerService,
    private readonly translate: TranslateService,
    public iqBoostStore: OutlookIqBoostStore,
  ) {}

  ngOnInit(): void {
    this.tracker.trackEvent({
      module: EventModule.IqBoost,
      journey: EventJourney.QuestionLibrary,
      action: EventAction.Opened,
    });
  }

  private buildEmailAssistGroup(): IqPromptGroup | null {
    const items = this.iqBoostStore.emailAssistPrompts();
    if (items.length === 0) return null;

    return {
      key: this.EMAIL_ASSIST_GROUP_KEY,
      label: this.translate.instant('outlook.iqBoost.emailAssist'),
      iconStyle: this.EMAIL_ASSIST_ICON_STYLE,
      items,
    };
  }

  isGroupCollapsed(key: string): boolean {
    return this.groupCollapse()[key] ?? false;
  }

  toggleGroup(key: string): void {
    const map = { ...this.groupCollapse() };
    map[key] = !this.isGroupCollapsed(key);
    this.groupCollapse.set(map);
  }

  onSuggestResult(event: {
    type: 'success' | 'error';
    messageKey: string;
  }): void {
    this.showSuggestPrompt.set(false);
    this.notification()?.showMessage(event.type, event.messageKey, true);
  }
}
